import { useState, useMemo, useEffect } from "react";
import LiveSubOrdersRenderer from "../SubscriptionDetails/Components/LiveSubOrdersRenderer.tsx";
import LiveSubInformationSection from "../SubscriptionDetails/Components/LiveSubInformationSection.tsx";
import { RootState, useAppSelector } from "../../../../state/state.tsx";
import { SubscriptionDocWithId } from "../../types/SubscriptionType.ts";
import { LiveSubscriptionType } from "../../types/LiveSubscriptionTypes.ts";
import Select from "../../../../assets/GifsAndPlaceholders/Select.png";
import AttendanceCalendar from "../SubscriptionDetails/Components/AttendanceCalendar.tsx";
import ProgressLogSection from "../SubscriptionDetails/Components/ProgressLogSection.tsx";
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Pagination from '@mui/material/Pagination';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { getSubscriptionStatusColorClass, getSubscriptionStatusText } from "../SubscriptionDetails/Components/LiveSubRenderer.tsx";
import SwapVertOutlinedIcon from '@mui/icons-material/SwapVertOutlined';
import { selectActiveRole, selectEffectiveUID } from "../../../../state/UserStatesV2/userSliceV2.ts";
import SyllabusSection from "../SubscriptionDetails/Components/SyllabusSection";
import { useNavigate } from "react-router-dom";

// Update ChevronIcon component
const ChevronIcon = ({ expanded }: { expanded: boolean }) => (
    <svg
        className={`w-5 h-5 transition-transform ${expanded ? 'rotate-180' : ''}`}
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <path d="M6 9l6 6 6-6" />
    </svg>
);

export default function AllSubscriptionsPage() {
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedStatus, setSelectedStatus] = useState<string>("All");
    const [selectedCycle, setSelectedCycle] = useState<string | null>(null);
    const [selectedSubscriberId, setSelectedSubscriberId] = useState<string | null>(null);
    const [showAttendance, setShowAttendance] = useState(false);
    const [showPayments, setShowPayments] = useState(false);
    const [showProgress, setShowProgress] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [sortType, setSortType] = useState<'remainingCount' | 'expiryDate' | 'default'>('default');
    const [dateFilterType, setDateFilterType] = useState<"all" | "last30" | "custom">("all");
    const [activeStudentTab, setActiveStudentTab] = useState<'payments' | 'attendance' | 'learning-status' | 'progress-log'>('payments');
    const activeRole = useAppSelector(selectActiveRole);
    const providerId = useAppSelector(selectEffectiveUID);
    const [customDateRange, setCustomDateRange] = useState<{ start: Date | null; end: Date | null }>({
        start: null,
        end: null
    });
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

    const ITEMS_PER_PAGE = 100;
    const Navigate = useNavigate();

    // Get all data from state with null checks
    const allLiveSubscriptions: LiveSubscriptionType[] = useAppSelector((state: RootState) =>
        state.LiveSubscriptionSlice?.data ?? []
    );
    const allSubscriptionPlans: SubscriptionDocWithId[] = useAppSelector((state: RootState) =>
        state.subscriptionTableSlice?.data ?? []
    );
    const allOrders = useAppSelector((state: RootState) =>
        state.OrderStatsSlice?.data ?? []
    );

    // Helper function to check if a subscription is active based on billing end date
    const isSubscriptionActive = (subscription: LiveSubscriptionType, plan: SubscriptionDocWithId | undefined) => {
        if (!plan) return false;

        if (plan.PaymentCycle === "MultiMonth") {
            if (!subscription.billingEnd) return false;
            // Convert Firebase timestamp to Date and set time to midnight
            const billingEndDate = new Date(subscription.billingEnd);
            billingEndDate.setHours(0, 0, 0, 0);

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            return today <= billingEndDate;
        }

        return subscription.status === "Active";
    };

    // Calculate total active subscribers with new logic
    const activeSubscribersCount = allLiveSubscriptions.length;

    // Helper function to get date 30 days ago
    const getLast30DaysDate = () => {
        const date = new Date();
        date.setDate(date.getDate() - 30);
        return date;
    };

    // Memoized filtered subscriptions with updated date filtering logic
    const filteredSubscriptions = useMemo(() => {
        let filtered = allLiveSubscriptions.filter(sub => {
            const matchesSearch =
                sub.studentName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                sub.phoneNumber?.includes(searchQuery) ||
                sub.id?.includes(searchQuery) ||
                sub.email?.toLowerCase().includes(searchQuery.toLowerCase());

            const matchesCycle = selectedCycle ? sub.subscriptionType === selectedCycle : true;

            // Handle status filtering
            let matchesStatus = true;
            if (selectedStatus === "Active") {
                matchesStatus = sub.status === "Active";
            } else if (selectedStatus === "All") {
                matchesStatus = true;
            } else if (selectedStatus === "Terminated") {
                matchesStatus = sub.status === "Terminated";
            } else if (selectedStatus === "InActive") {
                matchesStatus = sub.status === "InActive";
            } else if (selectedStatus === "Paused") {
                matchesStatus = sub.status === "Paused";
            } else {
                matchesStatus = !isSubscriptionActive(sub, allSubscriptionPlans.find(p => p.id === sub.subscriptionPlanRefId));
            }

            // Handle date filtering
            let matchesDate = true;
            const subscriptionStartDate = sub.startDate ? new Date(sub.startDate) : null;

            if (dateFilterType === "last30") {
                const thirtyDaysAgo = getLast30DaysDate();
                matchesDate = subscriptionStartDate ? subscriptionStartDate >= thirtyDaysAgo : true;
            } else if (dateFilterType === "custom" && customDateRange.start && customDateRange.end) {
                matchesDate = subscriptionStartDate ?
                    subscriptionStartDate >= customDateRange.start &&
                    subscriptionStartDate <= customDateRange.end : true;
            }

            return matchesSearch && matchesStatus && matchesCycle && matchesDate;
        });

        // Apply sorting based on sortType
        if (sortType === 'remainingCount') {
            filtered.sort((a, b) => {
                // If both are count type subscriptions, sort by remaining count
                if (a.subscriptionType === 'Count' && b.subscriptionType === 'Count') {
                    return (a.remainingCount || 0) - (b.remainingCount || 0);
                }

                // If only one is count type, prioritize it
                if (a.subscriptionType === 'Count') return -1;
                if (b.subscriptionType === 'Count') return 1;

                // For non-count subscriptions, maintain their relative order
                return 0;
            });
        } else if (sortType === 'expiryDate') {
            filtered.sort((a, b) => {
                // First, group by subscription type priority
                const getPriority = (type: string) => {
                    if (type === 'MultiMonth') return 0;
                    if (type === 'Count') return 1;
                    return 2; // Monthly
                };

                const priorityA = getPriority(a.subscriptionType);
                const priorityB = getPriority(b.subscriptionType);

                // If priorities are different, sort by priority
                if (priorityA !== priorityB) {
                    return priorityA - priorityB;
                }

                // If same priority, sort by expiry date in ascending order (nearest date first)
                // Use a very far future date for null/undefined billingEnd
                const farFutureDate = new Date('2099-12-31').getTime();
                const dateA = a.billingEnd ? new Date(a.billingEnd).getTime() : farFutureDate;
                const dateB = b.billingEnd ? new Date(b.billingEnd).getTime() : farFutureDate;
                return dateA - dateB; // Ascending order - nearest date first
            });
        }

        return filtered;
    }, [allLiveSubscriptions, searchQuery, selectedStatus, selectedCycle, dateFilterType, customDateRange, allSubscriptionPlans, sortType]);

    // Pagination
    const totalPages = Math.ceil(filteredSubscriptions.length / ITEMS_PER_PAGE);
    const paginatedSubscriptions = filteredSubscriptions.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    // Get selected subscriber and related data
    const selectedSubscriber = allLiveSubscriptions.find(sub => sub.id === selectedSubscriberId);
    const selectedSubscriptionPlan = selectedSubscriber
        ? allSubscriptionPlans.find(plan => plan.id === selectedSubscriber.subscriptionPlanRefId)
        : null;

    const selectedLiveSubscription = selectedSubscriber ? selectedSubscriber : null;
    const selectedSubscription = selectedSubscriptionPlan ? selectedSubscriptionPlan : null;
    const filteredOrders = allOrders.filter(order => order.livesubscriptionId === selectedSubscriberId);

    // Add click outside handler
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (isMenuOpen) {
                setIsMenuOpen(false);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [isMenuOpen]);

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-2 sm:p-4 md:p-6">
            {/* Left Panel */}
            <div className="border-[1px] border-t-cc_brand border-t-4 border-gray-300 bg-white rounded-xl shadow-lg p-3 sm:p-4">
                <h1 className="text-xl sm:text-2xl font-bold mb-4">All Students</h1>

                {/* Status Count */}
                <div className="mb-4 text-sm text-gray-600">
                    Total Subscribers: {activeSubscribersCount}
                </div>

                {/* Status Chips */}
                <div className="space-y-3 sm:space-y-4 mb-4">
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "flex-start", sm: "center" }}>
                        <span className="text-xs sm:text-sm text-gray-600 min-w-[80px]">Status:</span>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {["All", "Active", "InActive", "Terminated", "Paused"].map((status) => (
                                <Chip
                                    key={status}
                                    label={status}
                                    onClick={() => setSelectedStatus(status)}
                                    color={selectedStatus === status ? "primary" : "default"}
                                    variant={selectedStatus === status ? "filled" : "outlined"}
                                />
                            ))}
                        </Stack>
                    </Stack>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "flex-start", sm: "center" }}>
                        <span className="text-xs sm:text-sm text-gray-600 min-w-[80px]">Cycle:</span>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {["Monthly", "MultiMonth", "Count"].map((cycle) => (
                                <Chip
                                    key={cycle}
                                    label={cycle}
                                    onClick={() => setSelectedCycle(cycle === selectedCycle ? null : cycle)}
                                    color={selectedCycle === cycle ? "primary" : "default"}
                                    variant={selectedCycle === cycle ? "filled" : "outlined"}
                                />
                            ))}
                        </Stack>
                    </Stack>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "flex-start", sm: "center" }}>
                        <span className="text-xs sm:text-sm text-gray-600 w-[80px]">Start Date:</span>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Chip
                                label="All"
                                onClick={() => setDateFilterType("all")}
                                color={dateFilterType === "all" ? "primary" : "default"}
                                variant={dateFilterType === "all" ? "filled" : "outlined"}
                            />
                            <Chip
                                label="Last 30 Days"
                                onClick={() => setDateFilterType("last30")}
                                color={dateFilterType === "last30" ? "primary" : "default"}
                                variant={dateFilterType === "last30" ? "filled" : "outlined"}
                            />
                            <Chip
                                label="Custom Date"
                                onClick={() => {
                                    setDateFilterType("custom");
                                    setIsDatePickerOpen(true);
                                }}
                                color={dateFilterType === "custom" ? "primary" : "default"}
                                variant={dateFilterType === "custom" ? "filled" : "outlined"}
                            />
                        </Stack>
                    </Stack>
                </div>

                {/* Custom Date Range Dialog */}
                <Dialog open={isDatePickerOpen} onClose={() => {
                    setIsDatePickerOpen(false);
                    if (!customDateRange.start || !customDateRange.end) {
                        setDateFilterType("all");
                    }
                }}>
                    <DialogContent sx={{ paddingX: "60px", paddingY: "30px" }}>
                        <div className="text-xl font-semibold mb-4 ">
                            Select Date Range
                        </div>

                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <div className="flex flex-col gap-4 w-full sm:w-[300px]">
                                <div>
                                    <p className="text-xs font-medium mb-1 text-gray-600">Start Date</p>
                                    <DatePicker
                                        value={customDateRange.start ? dayjs(customDateRange.start) : null}
                                        onChange={(newValue) => {
                                            setCustomDateRange(prev => ({
                                                ...prev,
                                                start: newValue ? newValue.toDate() : null
                                            }));
                                        }}
                                        format="DD/MM/YYYY"
                                        slotProps={{
                                            textField: {
                                                size: "small",
                                                sx: {
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': {
                                                            borderColor: 'grey',
                                                            borderRadius: '10px',
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: 'grey',
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: 'skyblue',
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            fontSize: '14px',
                                                        },
                                                    },
                                                    width: '100%'
                                                }
                                            },

                                        }}
                                    />
                                </div>
                                <div>
                                    <p className="text-xs font-medium mb-1 text-gray-600">End Date</p>
                                    <DatePicker
                                        value={customDateRange.end ? dayjs(customDateRange.end) : null}
                                        onChange={(newValue) => {
                                            setCustomDateRange(prev => ({
                                                ...prev,
                                                end: newValue ? newValue.toDate() : null
                                            }));
                                        }}
                                        format="DD/MM/YYYY"
                                        minDate={customDateRange.start ? dayjs(customDateRange.start) : undefined}
                                        slotProps={{
                                            textField: {
                                                size: "small",
                                                sx: {
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': {
                                                            borderColor: 'grey',
                                                            borderRadius: '10px',
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: 'grey',
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: 'skyblue',
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            fontSize: '14px',
                                                        },
                                                    },
                                                    width: '100%'
                                                }
                                            },

                                        }}
                                    />
                                </div>
                            </div>
                        </LocalizationProvider>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => {
                            setIsDatePickerOpen(false);
                            setDateFilterType("all");
                            setCustomDateRange({ start: null, end: null });
                        }}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                if (customDateRange.start && customDateRange.end) {
                                    setIsDatePickerOpen(false);
                                }
                            }}
                            disabled={!customDateRange.start || !customDateRange.end}
                        >
                            Apply
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Search Bar */}
                <div className="relative mb-4">
                    <input
                        type="text"
                        placeholder="Search by name, phone, or email..."
                        value={searchQuery}

                        onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                                setSearchQuery('');
                            }
                        }}

                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setCurrentPage(1); // Reset to first page on new search
                        }}
                        className="w-full px-4 py-3 pl-10 rounded-lg border border-gray-300 focus:outline-none focus:border-cc_brand focus:ring-1 focus:ring-cc_brand"
                    />
                    <svg
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    </svg>
                </div>

                {/* Results Count */}
                <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-600 mb-4">
                        Showing {paginatedSubscriptions.length} of {filteredSubscriptions.length} results
                    </div>

                    {/* Custom Menu */}
                    <div className="relative mb-4 text-sm">
                        <div
                            className=" cursor-pointer flex items-center gap-1 px-4 py-2 bg-whiterounded-lg hover:bg-gray-50"
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsMenuOpen(!isMenuOpen);
                            }}
                        >
                            <SwapVertOutlinedIcon />
                            {sortType === "default" ? "Sort" : `Sort - ${sortType} `}

                        </div>
                        {isMenuOpen && (
                            <div
                                className="absolute z-10 mt-1 w-48 bg-white border border-gray-300 rounded-lg shadow-lg"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <button
                                    className="block w-full text-left px-4 py-2 hover:bg-gray-100 first:rounded-t-lg"
                                    onClick={() => {
                                        setSortType('remainingCount');
                                        setIsMenuOpen(false);
                                    }}
                                >
                                    Remaining Count
                                </button>
                                <button
                                    className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                                    onClick={() => {
                                        setSortType('expiryDate');
                                        setIsMenuOpen(false);
                                    }}
                                >
                                    Expiry Date
                                </button>
                                <button
                                    className="block w-full text-left px-4 py-2 hover:bg-gray-100 last:rounded-b-lg"
                                    onClick={() => {
                                        setSortType('default');
                                        setIsMenuOpen(false);
                                    }}
                                >
                                    Default
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Subscribers List */}
                <div className="bg-white rounded-lg">
                    {paginatedSubscriptions.length > 0 ? (
                        <div className="divide-y divide-gray-200 max-h-[calc(100vh-400px)] overflow-y-auto">
                            {paginatedSubscriptions.map((subscription) => {
                                const plan = allSubscriptionPlans.find(p => p.id === subscription.subscriptionPlanRefId);
                                const isSelected = selectedSubscriberId === subscription.id;

                                const statusColor = getSubscriptionStatusColorClass({
                                    status: subscription.status,
                                    subscriptionType: subscription.subscriptionType,
                                    remainingCount: subscription.remainingCount,
                                    billingEnd: subscription.billingEnd
                                });

                                return (
                                    <div
                                        key={subscription.id}
                                        className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${isSelected ? 'bg-gray-50 border-l-4 border-cc_brand' : ''
                                            }`}
                                        onClick={() => setSelectedSubscriberId(subscription.id)}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex-1">
                                                <h3 className="text-lg font-medium text-gray-900">
                                                    {subscription.studentName}
                                                </h3>
                                                <div className="mt-1 flex items-center gap-4 text-sm text-gray-500">
                                                    {subscription.phoneNumber && (
                                                        <span className="flex items-center gap-1">
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                                            </svg>
                                                            {subscription.phoneNumber}
                                                        </span>
                                                    )}
                                                    {subscription.email && (
                                                        <span className="flex items-center gap-1">
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                                                <polyline points="22,6 12,13 2,6"></polyline>
                                                            </svg>
                                                            {subscription.email}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div onClick= {() => {
                                                    Navigate(`/mysubscriptions/subscription/${plan?.id}`);
                                                }} className="text-sm font-medium text-cc_brand hover:underline cursor-pointer">
                                                    {plan?.Name || 'Unknown Plan'}
                                                </div>
                                                <div className={`mt-1 text-sm font-medium ${statusColor}`}>
                                                    {getSubscriptionStatusText({
                                                        status: subscription.status,
                                                        subscriptionType: subscription.subscriptionType,
                                                        remainingCount: subscription.remainingCount,
                                                        billingEnd: subscription.billingEnd
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="p-8 text-center">
                            <svg
                                className="mx-auto h-12 w-12 text-gray-400"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                aria-hidden="true"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                            <h3 className="mt-2 text-sm font-medium text-gray-900">No subscribers found</h3>
                            <p className="mt-1 text-sm text-gray-500">
                                Try adjusting your search terms or check back later.
                            </p>
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="mt-4 flex justify-center">
                        <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={(_, page) => setCurrentPage(page)}
                            color="primary"
                            size="large"
                        />
                    </div>
                )}
            </div>
            {/* Right Panel */}
            <div className="transition-all duration-300">
                {(selectedLiveSubscription && selectedSubscription) ? (
                    <div className="border-[1px] border-gray-300 h-full bg-white shadow-xl rounded-xl overflow-hidden">
                        {/* Student Info Section - Always Visible */}
                        <div className="p-4 border-b border-gray-200">
                            <h4 className="text-lg font-medium text-gray-900 mb-3">Student Information</h4>
                            {activeRole?.role !== "Observer" ? (
                                <LiveSubInformationSection
                                    serviceProviderId={providerId}
                                    selectedLiveSubDoc={selectedLiveSubscription}
                                    selectedSubscriptionPrice={selectedSubscription.Price}
                                    isAlign2monthBoundary={selectedSubscription.align2monthlyBoundary}
                                    NoGSTBatch={selectedSubscription.NoGSTBatch}
                                />
                            ) : (
                                <div className="text-center text-gray-500">
                                    <p className="text-lg font-medium text-gray-900 mb-3">Student Name: {selectedLiveSubscription.studentName}</p>
                                    <p className="text-sm font-medium text-gray-900 mb-3">ID: {selectedLiveSubscription.id}</p>
                                    <p>Student information is not available for observers.</p>
                                </div>
                            )}
                        </div>

                        {/* Student Details Tabs */}
                        <div className="border-b border-gray-200">
                            <div className="flex">
                                {activeRole?.role !== "Observer" && (
                                    <button
                                        onClick={() => setActiveStudentTab('payments')}
                                        className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${activeStudentTab === 'payments'
                                            ? 'bg-cc_brand text-white'
                                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                            }`}
                                    >
                                        Payments
                                    </button>
                                )}
                                {selectedSubscription.type !== "Rentals" && (
                                    <button
                                        onClick={() => setActiveStudentTab('attendance')}
                                        className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${activeStudentTab === 'attendance'
                                            ? 'bg-cc_brand text-white'
                                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                            }`}
                                    >
                                        Attendance
                                    </button>
                                )}
                                <button
                                    onClick={() => setActiveStudentTab('learning-status')}
                                    className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${activeStudentTab === 'learning-status'
                                        ? 'bg-cc_brand text-white'
                                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                        }`}
                                >
                                    Learning Status
                                </button>
                                <button
                                    onClick={() => setActiveStudentTab('progress-log')}
                                    className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${activeStudentTab === 'progress-log'
                                        ? 'bg-cc_brand text-white'
                                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                        }`}
                                >
                                    Progress Log
                                </button>
                            </div>
                        </div>

                        {/* Student Details Content */}
                        <div className="p-4 overflow-y-auto max-h-[calc(100vh-200px)]">
                            {/* Payments Tab */}
                            {activeStudentTab === 'payments' && (
                                <div>
                                    {filteredOrders.length > 0 ? (
                                        filteredOrders.map((data, index) => (
                                            <LiveSubOrdersRenderer OrderData={data} key={index} />
                                        ))
                                    ) : (
                                        <div className="flex flex-col items-center p-4 justify-center">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"
                                                fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                                strokeLinejoin="round" className="feather feather-info">
                                                <circle cx="12" cy="12" r="10"></circle>
                                                <line x1="12" y1="16" x2="12" y2="12"></line>
                                                <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                            </svg>
                                            <p>Please Initiate Payment</p>
                                            <p>No Orders Found!</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Attendance Tab */}
                            {activeStudentTab === 'attendance' && (
                                <div className="h-full">
                                    <AttendanceCalendar
                                        userId={selectedLiveSubscription.consumerId}
                                        livesubId={selectedLiveSubscription.id}
                                        subscriptionId={selectedSubscription.id || ''}
                                        livesubstatus={selectedLiveSubscription.status}
                                        userName={selectedLiveSubscription.studentName}
                                        subscriptionDays={selectedSubscription.days.map(day => Boolean(day))}
                                        subscriptionType={selectedSubscription.type}
                                        endDate={selectedLiveSubscription.billingEnd ? new Date(selectedLiveSubscription.billingEnd) : undefined}
                                        isCountHourly={Boolean(selectedSubscription.isCountHourly)}
                                        slots={[]}
                                        slotAssignments={{}}
                                    />
                                </div>
                            )}

                            {/* Learning Status Tab */}
                            {activeStudentTab === 'learning-status' && (
                                <div className="space-y-4">
                                    <div>
                                        <SyllabusSection
                                            subscription={selectedSubscription}
                                            providerId={providerId}
                                            liveSubId={selectedLiveSubscription.id}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Progress Log Tab */}
                            {activeStudentTab === 'progress-log' && (
                                <div className="space-y-4">
                                    <div>
                                        <ProgressLogSection
                                            liveSubId={selectedLiveSubscription.id}
                                            studentName={selectedLiveSubscription.studentName}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="border-[1px] border-gray-300 h-full bg-white shadow-xl rounded-xl flex items-center justify-center">
                        <div className="text-center text-gray-500">
                            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-4 text-gray-300">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                                <circle cx="9" cy="7" r="4"></circle>
                                <path d="m22 21-2-2"></path>
                                <path d="M16 16l4 4"></path>
                            </svg>
                            <p className="text-lg font-medium">Select a student</p>
                            <p className="text-sm">Choose a student from the list to view details</p>
                        </div>
                    </div>
                )}
            </div>


        </div>
    );
} 